/**
 * Splash Screen — заставка при запуске приложения
 *
 * Фиолетовый фон с концентрическими толстыми кольцами из центра и названием «Toive».
 * После анимации вызывает onComplete для перехода к основному контенту.
 *
 * Реализация на чистом CSS (без motion/framer-motion):
 *   - Пульсация колец: CSS keyframes `@keyframes splash-pulse` через scale transform
 *     (работает через GPU compositing, одинаково с motion, -80KB gzip в бандле).
 *   - Волновой эффект: разные `animation-delay` для каждого кольца (0.1s * index).
 *   - Fade-out: CSS `transition: opacity` + браузерный event `onTransitionEnd` для
 *     размонтирования. Никаких setState в useEffect — Compiler-friendly.
 *
 * Архитектура состояния (два простых state + derived value):
 *   - minTimeElapsed: ставится один раз через setTimeout (обычный useEffect)
 *   - unmounted:       ставится только в onTransitionEnd event handler
 *   - visible:         вычисляется прямо в рендере (derived, не state)
 *
 * Почему onTransitionEnd, а не setTimeout для размонтирования:
 *   setTimeout потребовал бы setState внутри useEffect — это запрещено плагином
 *   react-hooks v7+ (даже с guard-условием). onTransitionEnd — это DOM-event,
 *   setState в нём идиоматичен и не создаёт каскадных ре-рендеров.
 *
 * Защита от лишних срабатываний onTransitionEnd:
 *   transitionEnd триггерится на каждом transition (у нас пульсация через keyframes —
 *   это animation, не transition, поэтому конфликта нет, но на всякий случай
 *   проверяем propertyName === 'opacity' и e.target === e.currentTarget — чтобы
 *   не поймать пузырящиеся события от детей).
 */

import { useState, useEffect } from 'react';

/** Минимальная длительность показа заставки в мс (чтобы пульсация успела отыграть) */
const SPLASH_MIN_DURATION = 1800;
/** Длительность fade-out при скрытии в мс (должна совпадать с CSS `transition-duration`) */
const SPLASH_EXIT_DURATION = 600;

/**
 * Концентрические кольца — толстые полупрозрачные полосы из центра экрана.
 * radius — радиус кольца, thickness — толщина полосы, opacity — прозрачность.
 */
const RINGS = [
  { radius: 110, thickness: 35, opacity: 0.22 },
  { radius: 190, thickness: 40, opacity: 0.18 },
  { radius: 280, thickness: 45, opacity: 0.14 },
  { radius: 380, thickness: 48, opacity: 0.11 },
  { radius: 490, thickness: 50, opacity: 0.08 },
  { radius: 610, thickness: 52, opacity: 0.06 },
];

interface SplashScreenProps {
  /** Коллбэк после завершения анимации исчезновения */
  onComplete: () => void;
  /** Приложение загружено и готово к показу */
  isAppReady?: boolean;
}

export function SplashScreen({ onComplete, isAppReady = false }: SplashScreenProps): JSX.Element | null {
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [unmounted, setUnmounted] = useState(false);

  // Минимальное время показа — чтобы пульсация не обрезалась.
  // Это единственный useEffect, и он работает с внешней системой (setTimeout) —
  // setState в его коллбэке, не в теле. Это разрешённый паттерн.
  useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), SPLASH_MIN_DURATION);
    return () => clearTimeout(timer);
  }, []);

  // Derived state — вычисляется в рендере. Никакого setState не требуется.
  // Когда становится false → React обновит DOM (opacity: 0) →
  // CSS transition запустит fade-out → событие onTransitionEnd ниже размонтирует.
  const visible = !(minTimeElapsed && isAppReady);

  // После fade-out полностью скрываем компонент (чтобы он не ловил клики и т.п.)
  if (unmounted) return null;

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>): void => {
    // Ловим только нужный transition (opacity у самого splash-div, не у детей)
    if (e.propertyName !== 'opacity') return;
    if (e.target !== e.currentTarget) return;
    // Срабатываем только на fade-out (visible уже false)
    if (visible) return;

    setUnmounted(true);
    onComplete();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, var(--purple-medium) 0%, var(--purple-dark) 40%, #7468D4 100%)',
        opacity: visible ? 1 : 0,
        transition: `opacity ${SPLASH_EXIT_DURATION}ms ease-in-out`,
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      {/* Концентрические толстые кольца из центра экрана.
          Плавная пульсация — кольца "дышат" scale 1 ↔ 1.04 бесконечно.
          Каждое с небольшой задержкой (index * 0.1s) — получается волновой эффект. */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {RINGS.map(({ radius, thickness, opacity }, index) => (
          <div
            key={radius}
            className="absolute rounded-full"
            style={{
              width: radius * 2,
              height: radius * 2,
              border: `${thickness}px solid rgba(255, 255, 255, ${opacity})`,
              willChange: 'transform',
              animation: `splash-pulse 3s ease-in-out infinite`,
              animationDelay: `${index * 0.1}s`,
            }}
          />
        ))}
      </div>

      {/* Название по центру — без анимаций, появляется сразу с splash.
          font-family захардкожен системным — чтобы логотип рисовался мгновенно
          без FOUT (Lexend Deca self-hosted, подгружается с задержкой — иначе
          текст успевает моргнуть системным шрифтом, потом сменяется на Lexend). */}
      <h1
        className="relative z-10 text-white font-bold tracking-tight"
        style={{
          fontSize: '4.5rem',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        }}
      >
        Toive
      </h1>

      {/* CSS keyframes — inline в scope одного компонента.
          Альтернатива — вынести в styles/index.css глобально, но тут анимация
          нужна ровно в одном месте и не переиспользуется. */}
      <style>{`
        @keyframes splash-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
      `}</style>
    </div>
  );
}
